import { isExpired } from '@repo/utils/time'
import { jwtDecode } from 'jwt-decode'
import { collectFraudPreventionData } from './fraudHeaders'
import { ospreyAPI } from './ospreyAPI'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { useOutlookAddinStore } from '@repo/store/useOutlookAddinStore'
import axios from 'axios'
import Cookies from 'js-cookie'

// This module is a reusable http client for making API calls

// tokens for use with Osprey 2.x microservice APIs
let authToken
let authTokenExpiry
let refreshPromise = null

// timeout for token refresh attempts (ms)
const REFRESH_TIMEOUT_MS = 15000

// HMRC's fraud-prevention headers guide (SI 2019/360) requires these on every request
// that reaches HMRC's MTD ITSA endpoints. Detected from the resolved request path so
// this seam doesn't need to know about individual store calls.
const isItsaBoundUrl = (url) => {
  try {
    const path = new URL(url).pathname.replace(/^\//, '')
    return path.startsWith('HmrcMtdItsa/')
  } catch {
    return false
  }
}

const attachFraudPreventionHeaders = (headers) => {
  const fraudData = collectFraudPreventionData()
  headers['Gov-Client-Device-ID'] = fraudData.deviceId
  headers['Gov-Client-Timezone'] = fraudData.timezone
  headers['Gov-Client-Screens'] = fraudData.screens
  headers['Gov-Client-Window-Size'] = fraudData.windowSize
  headers['Gov-Client-Browser-JS-User-Agent'] = fraudData.jsUserAgent
  headers['Gov-Client-Browser-Do-Not-Track'] = String(fraudData.doNotTrack)
}

const withTimeout = (promise, ms, context = 'operation') => {
  let timeoutId
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`Timeout after ${ms}ms during ${context}`)), ms)
  })

  return Promise.race([promise.finally(() => clearTimeout(timeoutId)), timeout])
}

export const apiCall = async ({
  url,
  method = 'get',
  data = null,
  requiresAuth = true,
  setLoading = setIsLoading,
  headers = { 'Content-Type': 'application/json; charset=utf-8' },
  withCredentials = false,
  isLoggedIn = false,
  sendAsUrlParams = false,
  performTokenCheck = true,
  responseType = 'json',
  timeout = 60000,
  shouldTrimObjectStringValues = true
}) => {
  try {
    method = method.toUpperCase()

    if (requiresAuth) {
      // isLoggedIn means the user has previously logged in, it doesn't mean the token is present/still valid
      // but if it is false, we can definitely return because the user has not logged in and this endpoint requires auth
      if (!isLoggedIn) return Promise.reject(`Not logged in. Cannot call ${url}`)

      const validAuthToken = await getAuthTokenWithRefreshIfNeeded()
      headers.Authorization = `Bearer ${validAuthToken}`

      if (isItsaBoundUrl(url)) attachFraudPreventionHeaders(headers)

      if (performTokenCheck) {
        // check the token is for the correct site
        const decodedToken = jwtDecode(validAuthToken)
        const { companyCode, databaseSuffix } = useGlobalSettingsStore.getState().getDomainDetails()
        if (companyCode !== decodedToken.companyCode || databaseSuffix !== decodedToken.databaseSuffix) {
          throw new Error('Error: token is for wrong site', { cause: { status: 401 } })
        }
      }
    }

    const options = {
      method,
      url,
      headers,
      timeout,
      withCredentials,
      responseType,
      paramsSerializer: {
        serialize: (params) => {
          const searchParams = new URLSearchParams()
          Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined) return
            if (Array.isArray(value)) {
              value.forEach((item) => searchParams.append(key, item))
            } else {
              searchParams.append(key, String(value))
            }
          })
          return searchParams.toString()
        }
      }
    }

    let processedData = data

    if (data instanceof FormData) {
      processedData = data
    } else if (data && typeof data === 'object' && !Array.isArray(data)) {
      processedData = {}

      Object.keys(data).forEach((key) => {
        processedData[key] = shouldTrimObjectStringValues && typeof data[key] === 'string' ? data[key].trim() : data[key]
      })
    }

    if (method === 'GET' || sendAsUrlParams)
      options.params = processedData // if set, append trimmedData to the request as 'params' for GET requests
    else options.data = processedData // if set, append trimmedData to the request as body 'data' for POST, PUT, PATCH, DELETE requests

    setLoading(true)
    const res = await axios(options)
    setLoading(false)

    return res
  } catch (err) {
    console.log('apiCall error', err)
    setLoading(false)
    return Promise.reject(err)
  }
}

// refresh token management

const getAuthTokenWithRefreshIfNeeded = async () => {
  const refreshExpiry = Cookies.get('OspreyRefreshExpiry')
  const refreshTokenExpired = refreshExpiry ? isExpired(refreshExpiry) : true
  const authTokenExpired = authTokenExpiry ? isExpired(authTokenExpiry) : true

  // all good - we have a token and it hasn't expired
  if (authToken && !authTokenExpired) {
    return authToken
  }

  // try to get a new token using the refresh token if it hasn't expired
  if (!refreshTokenExpired) {
    try {
      // If no refresh is in progress, start one
      console.log('need to refresh token')

      if (!refreshPromise) {
        console.log('triggering token refresh')
        refreshPromise = withTimeout(performTokenRefresh(), REFRESH_TIMEOUT_MS, 'token refresh')
      }

      console.log('waiting for token refresh')
      await refreshPromise
      console.log('token refresh complete')
      refreshPromise = null // Clear after successful refresh
      return authToken
    } catch (err) {
      // If the refresh failed or timed out, clear refreshPromise and signal auth failure
      refreshPromise = null
      console.log('token refresh failed or timed out')
      throw new Error('Error: token refresh failed or timed out', {
        cause: { status: 401, reason: err?.message || 'unknown' }
      })
    }
  }

  const allCookies = Cookies.get()

  // uh oh, the user needs to reauthenticate
  throw new Error(
    `Error: Invalid or missing auth token. authTokenExpired: ${authTokenExpired}, authTokenExpiry: ${authTokenExpiry}, refreshTokenExpired: ${refreshTokenExpired}, refreshExpiry: ${refreshExpiry}, refreshPromise: ${!!refreshPromise}, allCookies: ${JSON.stringify(allCookies)}`,
    { cause: { status: 401 } }
  )
}

// Separate function to perform the actual token refresh
const performTokenRefresh = async () => {
  try {
    const data = await ospreyAPI({
      service: 'security',
      endpoint: 'User/RefreshAuthentication',
      method: 'POST',
      withCredentials: true
    })

    storeTokens({ params: data, tokenHasBeenRefreshed: true })
  } catch (err) {
    console.error('Error refreshing token', err)
    return Promise.reject(err)
  }
}

const updateAuthTokenInOsprey1IntervalMinutes = 10
const sendTokenToOsprey1 = () => {
  const { isLocal, baseURL, isOutlookAddin } = useGlobalSettingsStore.getState()
  if (isLocal || !authToken || isOutlookAddin) return

  const marginSeconds = 60 * updateAuthTokenInOsprey1IntervalMinutes + 10 // 10 minutes and 10 seconds. We want to make sure the token we pass is going to be valid until the next time we update it
  const authTokenExpired = authTokenExpiry ? isExpired(authTokenExpiry, marginSeconds) : true

  if (authTokenExpired) {
    // this will get a new token, and eventually call storeToken/sendTokenToOsprey1 again
    getAuthTokenWithRefreshIfNeeded()
  } else {
    const { passAuthTokenToOsprey1 } = useAuthenticationStore.getState()
    passAuthTokenToOsprey1({ authToken, baseURL })
  }
}

export const storeTokens = ({ params, tokenHasBeenRefreshed }) => {
  authToken = params.authenticationToken
  authTokenExpiry = params.authenticationTokenExpiryDateTime

  const { isOutlookAddin } = useGlobalSettingsStore.getState()
  if (isOutlookAddin) {
    useOutlookAddinStore.setState({ token: params })
  }

  // you can uncomment this if you want to test a token refresh. It will make it so the token is valid for one minute
  /*
  authTokenExpiry = new Date()
  authTokenExpiry.setMinutes(authTokenExpiry.getMinutes() + 1)
  authTokenExpiry = authTokenExpiry.toISOString()
  */

  // set this as a cookie, so new tabs can access it and work out whether there is a valid refresh token stored in cookies
  // javascript can't actually access the refresh token itself, but can trigger a call to the refresh token endpoint with the token cookie attached
  Cookies.set('OspreyRefreshExpiry', params.refreshTokenExpiryDateTime)

  if (tokenHasBeenRefreshed) {
    // TODO await?
    sendTokenToOsprey1()
  }
}

// we're going to pass the auth token to Osprey 1 every 10 minutes, to keep the session alive
let passAuthTokenToOsprey1Interval = null
if (passAuthTokenToOsprey1Interval) clearInterval(passAuthTokenToOsprey1Interval)
passAuthTokenToOsprey1Interval = setInterval(sendTokenToOsprey1, 1000 * 60 * updateAuthTokenInOsprey1IntervalMinutes) // every updateAuthTokenInOsprey1IntervalMinutes minutes

export const removeTokens = () => {
  authToken = null
  authTokenExpiry = null
  Cookies.remove('OspreyRefreshExpiry')
}

const setIsLoading = (bool) => {
  useLoadingStore.setState({ isLoading: bool })
}
